// ─────────────────────────────────────────────────────────────────────────────
//  DatabaseBackupDialog
//
//  The System-Administrator confirmation + download for POST
//  /downloadDatabaseBackup (a gzipped mysqldump of the whole database,
//  streamed straight to the browser). One component, used in two places:
//
//    • Admin Panel → Database Backup          (a logged-in administrator)
//    • ServiceSuspended, the lapsed-subscription screen — a facility whose
//      subscription has ended can still take its own data with it. The
//      endpoint checks the admin username/password itself and needs no
//      session, which is why it works there although login is refused.
//
//  `zIndex`: ServiceSuspended is a fixed overlay at z-index 20000, above
//  MUI's dialog layer (1300) — without lifting the dialog it would open
//  invisibly behind that screen. For the same reason the result is shown
//  inside the dialog rather than in a SweetAlert, which would sit at 1060.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Button, TextField, Stack, Alert, CircularProgress,
  RadioGroup, FormControlLabel, Radio, Typography,
} from '@mui/material';
import { CloudDownloadOutlined as BackupIcon, CheckCircleOutline as DoneIcon } from '@mui/icons-material';

import { downloadDatabaseBackup } from '../BaseURL';

const DatabaseBackupDialog = ({ open, onClose, zIndex, intro }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  // 'sql' — a plain .sql file that opens or imports anywhere (the default).
  // 'gz'  — the same dump compressed; far smaller for a large database.
  const [format, setFormat] = useState('sql');

  // Fresh form every time it opens.
  useEffect(() => {
    if (open) { setUsername(''); setPassword(''); setError(''); setSaved(''); setFormat('sql'); }
  }, [open]);

  const close = () => { if (!downloading) onClose(); };

  const runDownload = async () => {
    if (!username.trim() || !password) {
      setError('Username and password are required.');
      return;
    }
    setError('');
    setDownloading(true);
    try {
      // Long-running: expect up to a few minutes for a big DB. fetch + blob
      // so the browser downloads without navigating away from the page.
      const response = await fetch(downloadDatabaseBackup, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password, format }),
      });

      // The backend answers JSON on error (auth / mysqldump missing) BEFORE
      // any data is streamed. Detect that by the Content-Type.
      const ct = response.headers.get('Content-Type') || '';
      if (!response.ok || ct.startsWith('application/json')) {
        const data = ct.startsWith('application/json') ? await response.json() : { message: `HTTP ${response.status}` };
        throw new Error(data?.message || 'Backup failed.');
      }

      const blob = await response.blob();
      const disp = response.headers.get('Content-Disposition') || '';
      const match = /filename="?([^"]+)"?/i.exec(disp);
      const filename = match?.[1] || `database_backup_${Date.now()}.sql${format === 'gz' ? '.gz' : ''}`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);

      setPassword('');
      setSaved(filename);
    } catch (err) {
      setError(err?.message || 'Backup failed.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth
      sx={zIndex ? { zIndex } : undefined}>
      <DialogTitle sx={{ pb: 1 }}>
        {saved ? 'Database downloaded' : 'Confirm — System Administrator'}
      </DialogTitle>
      <DialogContent>
        {saved ? (
          <Alert icon={<DoneIcon />} severity="success" variant="outlined" sx={{ fontSize: '0.85rem' }}>
            <b>{saved}</b> has been saved to this computer (usually the Downloads folder).
            Keep it somewhere safe — it holds every patient record.
          </Alert>
        ) : (
          <>
            <DialogContentText sx={{ mb: 1.5, fontSize: '0.85rem' }}>
              {intro || 'Downloading the full database is restricted to System Administrators. '
                + 'Enter your admin credentials to start the backup.'}
            </DialogContentText>
            <Stack spacing={1.25} component="form" onSubmit={(e) => { e.preventDefault(); runDownload(); }}>
              <TextField label="Username" size="small" value={username} autoFocus fullWidth
                autoComplete="username" disabled={downloading}
                onChange={(e) => setUsername(e.target.value)} />
              <TextField label="Password" type="password" size="small" value={password} fullWidth
                autoComplete="current-password" disabled={downloading}
                onChange={(e) => setPassword(e.target.value)} />
              <RadioGroup value={format} onChange={(e) => setFormat(e.target.value)} sx={{ pl: 0.5 }}>
                <FormControlLabel value="sql" disabled={downloading} control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: '0.85rem' }}><b>.sql file</b> — opens and imports anywhere</Typography>} />
                <FormControlLabel value="gz" disabled={downloading} control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: '0.85rem' }}><b>.sql.gz</b> — compressed, much smaller download</Typography>} />
              </RadioGroup>
              {/* Lets Enter submit the form. */}
              <button type="submit" hidden aria-hidden tabIndex={-1} />
              {error && <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>{error}</Alert>}
              {downloading && (
                <Alert severity="info" variant="outlined" sx={{ py: 0.25 }}>
                  Preparing the backup. Please don't close this tab — this can take a few minutes on larger databases.
                </Alert>
              )}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        {saved ? (
          <Button variant="contained" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button onClick={close} disabled={downloading}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={runDownload} disabled={downloading}
              startIcon={downloading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <BackupIcon />}>
              {downloading ? 'Downloading…' : 'Start Backup'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DatabaseBackupDialog;
